export interface IOpportunityCategory {
  id: number
  title: string
  _created: Date
}

export interface IOpportunity {
  id: number
  user_id: number
  opportunity_type: string
  current_gpi: string | null
  target_gpi_1: string | null
  year_savings: number
  client: string
  _created: Date
}

export interface IOpportunityFromCategoryResponse {
  total_events: number
  page_size: number
  page: number
  has_next_page: boolean
  results: IOpportunity[]
}

// status of a medication opportunity, as the status selects set and filter it
export enum StatusType {
  PHARMD_APPROVAL_NEEDED = 1,
  MEMBER_OUTREACH_NEEDED,
  MEMBER_PENDING,
  DOCTOR_OUTREACH_NEEDED,
  DOCTOR_PENDING,
  PHARMACY_PENDING,
  PHARMD_DENIED,
  MEMBER_DENIED,
  DOCTOR_DENIED,
  PHARMACY_APPROVED,
  PHARMACY_DENIED
}

export type Status = {
  comment?: string
  created: string
  opportunity_id: number
  type: StatusType
}

export enum OpportunityStatus {
  DEFAULT = 1,
  APPROVED = 2,
  REJECTED = 3,
  ACTIVATED = 4
}

export enum OpportunityStatusName {
  DEFAULT = 'Default',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
  ACTIVATED = 'Activated'
}

export interface IOpportunityDetailsQuestion {
  id?: number
  text: string
  description: string
}

export interface IOpportunityDetails {
  id: string
  status: OpportunityStatus
  generated_id?: string | null
  opportunity_type?: string | null
  rule_name?: string | null
  opportunity_status?: string | null
  latest_dos?: string | null
  related_dos_array?: string[] | null
  adherence_ratio?: number | null
  attributed_member_year_savings?: number | null
  attributed_sponsor_year_savings?: number | null
  current_awp?: string | null
  current_drug_name?: string | null
  current_drug_strength?: string | null
  current_ndc?: string | null
  expected_refill_date?: string | null
  year_savings?: number | null
  category?: string | null
  client?: string | null
  note?: string | null
  recommendation?: string | null
  questions?: IOpportunityDetailsQuestion[] | null
  target_gpi_1?: string | null
  target_ndc_1?: string[] | null
}

export enum EventSourceEnum {
  PharmacistSurfaced = 1,
  MemberSurfaced = 2,
  RulesSurfaced = 3
}

export interface OpportunityStore {
  categories: IOpportunityCategory[]
  uniqueRules: IOpportunityDetails[]

  findOpportunityCategoryById: (id: number) => IOpportunityCategory | undefined
  fetchOpportunityCategories: () => Promise<boolean>
  fetchOpportunitiesUniqueRules: () => Promise<boolean>
  getOpportunitiesUniqueRulesByName: (
    ruleName: string
  ) => IOpportunityDetails | undefined
  getOpportunitiesFromCategories: (
    categoryId: number,
    page: number,
    pageSize: number
  ) => Promise<IOpportunityFromCategoryResponse | null>
  getOpportunityDetailsFromId: (
    id: string
  ) => Promise<IOpportunityDetails | null>
  getOpportunitiesByUserId: (
    userId: number
  ) => Promise<IOpportunityDetails[] | null>
  getRuleQuestionsByUserId: (
    ruleName: string,
    userId: number
  ) => Promise<IOpportunityDetailsQuestion[] | null>
  setRuleQuestionsByUserId: (
    ruleName: string,
    userId: number,
    questions: IOpportunityDetailsQuestion[]
  ) => Promise<{ success: boolean; errorMessage?: string }>
  upsertOpportunityUniqueRule: (
    rule_name: string,
    questions: IOpportunityDetailsQuestion[]
  ) => Promise<boolean>
  activateOpportunityByUserId: (
    userId: number,
    ruleName: string
  ) => Promise<boolean>
  deactivateOpportunityByUserId: (
    userId: number,
    ruleName: string
  ) => Promise<boolean>
  approveOpportunity: (opportunityId: string) => Promise<boolean>
  rejectOpportunity: (opportunityId: string) => Promise<boolean>
  clear: () => void
}
